import { createFileRoute } from '@tanstack/react-router';
import { Logo } from '@/app/ui/components/logo';
import { Card } from '@/app/ui/components/card';
import { useTranslations } from 'use-intl';
import * as React from 'react';
import { useAuth } from '@/app/utils/auth';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Field, FieldControl, FieldError, FieldLabel } from '@/app/ui/components/form/field';
import { Button } from '@/app/ui/components/button';
import { validator } from '@/app/validator';
import { Navigate } from '@tanstack/react-router';

export const Route = createFileRoute('/(auth)/_layout/login')({
  component: RouteComponent,
  validateSearch: validator(
    z.object({
      email: z.string().optional(),
      target: z.string().optional(),
      activationSuccess: z.boolean().optional(),
      inviteAccepted: z.boolean().optional(),
      organizationName: z.string().optional(),
    }),
  ),
});

function RouteComponent() {
  const t = useTranslations();
  const search = Route.useSearch();
  const email = search.email ?? '';
  const redirectTarget = search.target ?? '';
  const activationSuccess = search.activationSuccess;

  const { isAuthenticated, isLoading, login } = useAuth();

  const [isFormLoading, setIsFormLoading] = React.useState(false);

  const schema = z.object({
    email: z
      .string()
      .min(1, { message: t('pages.login.form.validations.emailRequired') })
      .email({ message: t('pages.login.form.validations.invalidEmail') })
      .max(254),
  });
  const { control, handleSubmit } = useForm({
    defaultValues: { email: decodeURIComponent(email) },
    resolver: zodResolver(schema),
  });

  const [loginError, setLoginError] = React.useState('');

  const onSubmit = async (values: z.infer<typeof schema>) => {
    setIsFormLoading(true);
    setLoginError('');
    try {
      await login({
        appState: {
          returnTo: redirectTarget || undefined,
          activationSuccess: activationSuccess || undefined,
        },
        loginHint: values.email.replace(/\+/g, '%2B'),
      });
    } catch {
      setLoginError(t('general.somethingWentWrong'));
    } finally {
      setIsFormLoading(false);
    }
  };

  if (!isLoading && !isAuthenticated) {
    return (
      <div className="px-5 -translate-y-5 w-full">
        <Logo className="mb-5 mx-auto" />

        <Card className="max-w-92 lg:p-6 p-5 mx-auto w-full">
          <h1 className="text-center font-semibold text-lg mb-0.5 lg:text-xl">
            {t('pages.login.heading')}
          </h1>

          <p className="text-center text-muted text-sm mb-4">{t('pages.login.auth0.subheading')}</p>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <Controller
              name="email"
              control={control}
              render={({
                field: { ref, name, value, onBlur, onChange },
                fieldState: { invalid, isTouched, isDirty, error },
              }) => (
                <Field name={name} invalid={invalid} touched={isTouched} dirty={isDirty}>
                  <FieldLabel>{t('pages.login.formLabels.email')}</FieldLabel>
                  <FieldControl
                    autoFocus
                    ref={ref}
                    value={value}
                    onBlur={onBlur}
                    onValueChange={onChange}
                    type="email"
                    placeholder={t('pages.login.form.emailPlaceholder')}
                  />
                  <FieldError match={!!error}>{error?.message}</FieldError>
                </Field>
              )}
            />

            {loginError && <p className="text-destructive text-sm text-center">{loginError}</p>}

            <Button size="lg" type="submit" pending={isFormLoading} className="w-full">
              {t('pages.login.auth0.continue')}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-4">
            <div className="h-px flex-1 bg-border" />
            <span className="text-muted text-xs">{t('general.or')}</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <Button
            size="lg"
            variant="secondary"
            className="w-full"
            onClick={() =>
              login({
                connection: import.meta.env.VITE_AUTH0_SSO_CONNECTION,
                appState: {
                  returnTo: redirectTarget || undefined,
                  activationSuccess: activationSuccess || undefined,
                },
              })
            }
          >
            {t('pages.login.ssoLogin')}
          </Button>
        </Card>
      </div>
    );
  }

  return <Navigate to={'/'} />;
}
